import { useCallback, useEffect } from 'react';

import { useApolloClient } from '@apollo/react-hooks';
import { ApolloQueryResult } from 'apollo-client';

import { ICartEntry } from '@rbi-ctg/menu';
import {
  IBenefitSwap,
  ILoyaltyOffersQuery,
  ILoyaltyUserOffersQuery,
  IOffersFragment,
  LoyaltyUserOffersDocument,
  OfferRedemptionType,
  PaymentMethod,
  useLoyaltyOffersLazyQuery,
  useLoyaltyUserOffersLazyQuery,
} from 'generated/graphql-gateway';
import { OfferType } from 'generated/rbi-graphql';
import {
  useFeatureSortedLoyaltyOffersQuery,
  useLoyaltySystemwideOffersByEngineIdQuery,
} from 'generated/sanity-graphql';
import { useLocaleSmartBlockContent } from 'hooks/use-locale-smart-block-content/use-locale-smart-block-content';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import {
  flattenEntriesToMap,
  parseEntry,
  parseOffers,
} from 'state/global-state/models/loyalty/offers/offers.utils';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useServiceModeContext } from 'state/service-mode';
import { useStoreContext } from 'state/store';
import logger from 'utils/logger';

import { LoyaltyAppliedOffer, LoyaltyOffer } from '../types';

import {
  IAppliedRewards,
  IQueryLoyaltyUserOffersOptions,
  IncentiveEvaluationErrorCodes,
  isSwap,
} from './types';
import { usePersonalizedOffers } from './use-personalized-offers';
import { IPersonalizedData, mergePersonalizedData } from './utils/personalized-offers';

export const useLoyaltyOffers = () => {
  // This flag is parent to the next ones
  const loyaltyOffersEnabled = Boolean(useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_OFFERS));
  // This flag replaces Offers 3.0
  const loyaltyStandardOffersEnabled = Boolean(
    useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_STANDARD_OFFERS)
  );
  // This flag enables surprise offers including swaps
  const loyaltySurpriseOffersEnabled = Boolean(
    useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_SURPRISE_OFFERS)
  );

  const enableLoyaltyStandardOffers = useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_STANDARD_OFFERS);
  const offersCooldownEnabled = Boolean(useFlag(LaunchDarklyFlag.ENABLE_GLOBAL_OFFERS_COOLDOWN));

  const client = useApolloClient();
  const dispatch = useAppDispatch();
  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const userOffers = useAppSelector(selectors.loyalty.selectUserOffers);
  const cartEntriesIdsMap = useAppSelector(selectors.loyalty.selectCartEntriesIdsMap);
  const { serviceMode } = useServiceModeContext();
  const { store } = useStoreContext();
  const [
    fetchPersonalizedOfferData,
    { loading: personalizedDataLoading },
  ] = usePersonalizedOffers();
  const storeId = store?.number;

  const { transformSmartBlockContent } = useLocaleSmartBlockContent();
  const {
    loading: cmsOffersLoading,
    refetch: refetchCmsOffers,
  } = useFeatureSortedLoyaltyOffersQuery({
    skip: !enableLoyaltyStandardOffers,
    variables: {
      id: 'feature-loyalty-offers-ui-singleton',
    },
    onCompleted(data) {
      if (data?.LoyaltyOffersUI?.sortedSystemwideOffers) {
        dispatch(
          actions.loyalty.setCmsOffers(
            data.LoyaltyOffersUI.sortedSystemwideOffers as LoyaltyOffer[]
          )
        );
      }
    },
  });
  const getValidOfferWithSwap = (offer: IOffersFragment) => {
    const isValidSwapOffer = (singleBenefit: IBenefitSwap) => {
      // Checking swap from value is in cartEntries
      const isValidSwap = cartEntriesIdsMap[singleBenefit.value.from];

      if (isValidSwap) {
        dispatch(actions.loyalty.setUpsizeAvailable(true));
      }
      return isValidSwap;
    };

    // Filtering valid benefits from offer
    const benefits = offer?.benefits?.filter(benefit => {
      if (isSwap(benefit)) {
        return isValidSwapOffer(benefit);
      }
      // Offer is not a swap so don't filter it
      return true;
    });

    return { ...offer, ...(benefits && { benefits }) };
  };

  const getPersonalizedOffer = (
    offer: IOffersFragment,
    personalizedOfferData: IPersonalizedData
  ): LoyaltyOffer | undefined => {
    let personalizedOffer;

    try {
      personalizedOffer = mergePersonalizedData({
        engineOffer: offer,
        transformSmartBlockContent,
        ...personalizedOfferData,
      });
    } catch (error) {
      const message = error?.message ? `Error: ${error.message}` : '';
      logger.error(`Failed processing personalized offer: ${offer.id} - ${message}`);
    }

    return personalizedOffer;
  };

  const onUserOffersQueryComplete = async (data: ILoyaltyUserOffersQuery) => {
    const loyaltyUser = data?.loyaltyUser;
    const offerRedemptionAvailableAfter = loyaltyUser?.offerRedemptionAvailability?.availableAfter;

    if (offerRedemptionAvailableAfter && offersCooldownEnabled) {
      dispatch(actions.loyalty.setOfferRedemptionAvailableAfter(offerRedemptionAvailableAfter));
    }

    const loyaltyUserOffers = loyaltyUser?.offers;
    if (!loyaltyUserOffers?.length) {
      return;
    }

    // Fetch data needed to build personalized offer
    const personalizedOfferData = await fetchPersonalizedOfferData(loyaltyUserOffers);

    let personalizedUserOffers: LoyaltyOffer[] = [];
    let systemWideOffers: IOffersFragment[] = [];

    loyaltyUserOffers.forEach(offer => {
      let validOffer = offer;

      // Do not validate surprise offers if feature is not enabled
      if (loyaltySurpriseOffersEnabled) {
        validOffer = getValidOfferWithSwap(offer);
        dispatch(actions.loyalty.setSurpriseOfferIfAvailable(offer));
      }

      if (validOffer.type === OfferType.PERSONALIZED && personalizedOfferData) {
        const personalizedOffer = getPersonalizedOffer(validOffer, personalizedOfferData);
        if (personalizedOffer) {
          personalizedUserOffers.push(personalizedOffer);
        }
      }

      if (validOffer.type === OfferType.GLOBAL) {
        systemWideOffers.push(validOffer);
      }
    });

    dispatch(actions.loyalty.setUserOffers(systemWideOffers));
    dispatch(actions.loyalty.setPersonalizedOffers(personalizedUserOffers));
  };

  const [queryUserOffers, { loading: queryUserOffersLoading }] = useLoyaltyUserOffersLazyQuery({
    fetchPolicy: 'no-cache',
    onCompleted: onUserOffersQueryComplete,
  });

  const [queryEngineOffers, { loading: engineOffersLoading }] = useLoyaltyOffersLazyQuery({
    fetchPolicy: 'network-only',
    variables: {
      serviceMode: serviceMode || undefined,
      storeId,
      omitInvalids: false,
    },
    onCompleted: (data: ILoyaltyOffersQuery) => {
      if (data?.loyaltyOffers?.length) {
        dispatch(actions.loyalty.setOffers(data.loyaltyOffers as IOffersFragment[]));
      }
    },
  });

  // Find all surprise offers ids for the current user
  const surpriseOffersEngineIds = userOffers.reduce((acc: string[], offer) => {
    if (offer.redemptionType === OfferRedemptionType.SURPRISE) {
      acc.push(offer.id);
    }
    return acc;
  }, []);

  // Attach applied offers ids to get the full collection of offers from CMS
  const appliedLoyaltyOffersIds = surpriseOffersEngineIds.concat(
    appliedOffers.map(({ id }) => id || '')
  );

  const { loading: swoffersLoading } = useLoyaltySystemwideOffersByEngineIdQuery({
    variables: {
      ids: appliedLoyaltyOffersIds,
    },
    skip: !appliedLoyaltyOffersIds.length,
    onCompleted(data) {
      if (data?.allSystemwideOffers) {
        dispatch(actions.loyalty.setCmsSurpriseOffers(data.allSystemwideOffers as LoyaltyOffer[]));
      }
    },
  });

  const queryLoyaltyUserOffers = useCallback(
    (redemptionTypes: OfferRedemptionType[]) => ({
      loyaltyId,
      cartEntries,
      appliedRewards,
      subtotalAmount,
    }: IQueryLoyaltyUserOffersOptions) => {
      const parsedEntries = cartEntries?.reduce(parseEntry(appliedRewards), []);
      dispatch(
        actions.loyalty.setCartEntriesIdsMap((parsedEntries || []).reduce(flattenEntriesToMap, {}))
      );
      queryUserOffers({
        variables: {
          loyaltyId,
          where: {
            appliedIncentives: parseOffers(appliedOffers),
            cartEntries: parsedEntries,
            serviceMode: serviceMode || undefined,
            redemptionTypes,
            storeId,
            subtotalAmount,
            omitInvalids: false,
          },
        },
      });
    },
    [queryUserOffers, dispatch, appliedOffers, serviceMode, storeId]
  );

  // Offers without loyaltyId should always be of redemption type STANDARD
  const queryLoyaltyOffers = useCallback(
    ({ subtotalAmount = 0 }) => {
      queryEngineOffers({
        variables: {
          serviceMode,
          storeId,
          subtotalAmount,
          redemptionTypes: [OfferRedemptionType.STANDARD],
        },
      });
    },
    [queryEngineOffers, serviceMode, storeId]
  );

  const queryLoyaltyUserStandardOffers = useCallback(
    queryLoyaltyUserOffers([OfferRedemptionType.STANDARD]),
    [queryLoyaltyUserOffers]
  );

  const queryLoyaltyUserSurpriseOffers = useCallback(
    queryLoyaltyUserOffers([OfferRedemptionType.SURPRISE, OfferRedemptionType.SWAP]),
    [queryLoyaltyUserOffers]
  );

  const evaluateLoyaltyUserIncentives = useCallback(
    async (
      loyaltyId: string,
      appliedLoyaltyOffers: LoyaltyAppliedOffer[],
      cartEntries?: ICartEntry[],
      appliedLoyaltyRewards?: IAppliedRewards | null,
      subtotalAmount?: number,
      paymentMethod?: PaymentMethod | null
    ) => {
      const parsedEntries = cartEntries?.reduce(parseEntry(appliedLoyaltyRewards), []);
      dispatch(
        actions.loyalty.setCartEntriesIdsMap((parsedEntries || []).reduce(flattenEntriesToMap, {}))
      );
      dispatch(actions.loyalty.setOffersLoading(true));
      try {
        const appliedOffersIds = appliedLoyaltyOffers.map(({ id }) => id);
        const result: ApolloQueryResult<ILoyaltyUserOffersQuery> = await client.query({
          query: LoyaltyUserOffersDocument,
          fetchPolicy: 'network-only',
          variables: {
            loyaltyId,
            where: {
              cartEntries: parsedEntries,
              ids: appliedOffersIds,
              omitInvalids: false,
              paymentMethod,
              serviceMode: serviceMode || undefined,
              storeId,
              subtotalAmount,
            },
          },
        });
        if (result.data?.loyaltyUser?.offers) {
          const { offerFeedbackMap, validIds } = result.data.loyaltyUser.offers.reduce(
            (acc, offer) => {
              acc.validIds.add(offer.id);
              if (offer?.errors?.length) {
                acc.offerFeedbackMap[offer.id] = offer.errors;
              }
              return acc;
            },
            { offerFeedbackMap: {}, validIds: new Set() }
          );

          appliedOffersIds.forEach(id => {
            if (id && !validIds.has(id)) {
              offerFeedbackMap[id] = [{ code: IncentiveEvaluationErrorCodes.OFFER_NOT_AVAILABLE }];
            }
          });

          dispatch(actions.loyalty.setOffersFeedbackMap(offerFeedbackMap));

          return offerFeedbackMap;
        }
      } finally {
        dispatch(actions.loyalty.setOffersLoading(false));
      }

      return null;
    },
    [dispatch, client, serviceMode, storeId]
  );

  useEffect(() => {
    dispatch(
      actions.loyalty.setOffersLoading(
        engineOffersLoading ||
          queryUserOffersLoading ||
          personalizedDataLoading ||
          cmsOffersLoading ||
          swoffersLoading
      )
    );
  }, [
    dispatch,
    engineOffersLoading,
    queryUserOffersLoading,
    personalizedDataLoading,
    cmsOffersLoading,
    swoffersLoading,
  ]);

  return {
    evaluateLoyaltyUserIncentives,
    loyaltyOffersEnabled,
    loyaltyStandardOffersEnabled,
    loyaltySurpriseOffersEnabled,
    queryLoyaltyOffers,
    queryLoyaltyUserStandardOffers,
    queryLoyaltyUserSurpriseOffers,
    refetchCmsOffers,
  };
};
